import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Smooth crossfade carousel for the hero image.
 * - Long, gentle Ken Burns zoom on the active slide.
 * - Auto-advance every `interval` ms with pause-on-hover.
 * - Custom dots + tiny chevron controls.
 * - Per-slide caption (label + sublabel) animates in.
 */
const HeroCarousel = ({ slides, interval = 5000 }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (next) => setIndex((prev) => (next + slides.length) % slides.length),
    [slides.length],
  );

  useEffect(() => {
    if (paused) return;
    const id = setTimeout(() => go(index + 1), interval);
    return () => clearTimeout(id);
  }, [index, interval, paused, go]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.1, ease: [0.21, 0.47, 0.32, 0.98] }}
      className="relative aspect-[4/5] w-full overflow-hidden rounded-lg ring-1 ring-black/5 group"
      data-testid="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Crossfading images with subtle Ken Burns zoom */}
      <AnimatePresence initial={false}>
        <motion.img
          key={slides[index].src}
          src={slides[index].src}
          alt={slides[index].label}
          data-testid={`hero-slide-${index}`}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{
            opacity: { duration: 1.4, ease: [0.21, 0.47, 0.32, 0.98] },
            scale: { duration: 6.5, ease: "linear" },
          }}
          className="absolute inset-0 h-full w-full object-cover will-change-transform"
        />
      </AnimatePresence>

      {/* Vignette */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0 pointer-events-none" />

      {/* Caption */}
      <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={`cap-${index}`}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98] }}
          >
            <p
              className="text-eyebrow"
              style={{ color: "rgba(255,255,255,0.85)" }}
            >
              {slides[index].label}
            </p>
            <p className="text-white font-display text-xl sm:text-2xl mt-1">
              {slides[index].sublabel}
            </p>
          </motion.div>
        </AnimatePresence>

        <div className="rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-[11px] font-medium text-[color:var(--nitc-ink)] tracking-wider">
          EST. 1998
        </div>
      </div>

      {/* Controls (visible on hover, always on touch) */}
      <button
        onClick={() => go(index - 1)}
        aria-label="Previous slide"
        data-testid="hero-carousel-prev"
        className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/85 backdrop-blur text-[color:var(--nitc-ink)] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-white transition-opacity duration-300 z-10"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={() => go(index + 1)}
        aria-label="Next slide"
        data-testid="hero-carousel-next"
        className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/85 backdrop-blur text-[color:var(--nitc-ink)] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-white transition-opacity duration-300 z-10"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Dots */}
      <div className="absolute top-5 right-5 flex flex-col gap-2 z-10">
        {slides.map((s, i) => (
          <button
            key={s.src}
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`hero-carousel-dot-${i}`}
            className="relative h-1 w-7 overflow-hidden rounded-full bg-white/30"
          >
            <motion.span
              className="block h-full bg-white"
              initial={false}
              animate={{ width: i === index ? "100%" : "0%" }}
              transition={{
                duration: i === index && !paused ? interval / 1000 : 0.4,
                ease: "linear",
              }}
            />
          </button>
        ))}
      </div>
    </motion.div>
  );
};

export default HeroCarousel;
